import React, { useState } from 'react';
import { Meh, Smile, Laugh } from 'lucide-react';
import { applyNoZoomViewport, restoreViewport } from '@/lib/utils';

interface FeedbackUIProps {
    onSubmit: (rating: number, comment?: string) => void;
    primaryColor: string;
    secondaryColor: string;
}

const RATING_OPTIONS = [
    { Icon: Meh, rating: 3, label: 'Neutral' },
    { Icon: Smile, rating: 4, label: 'Happy' },
    { Icon: Laugh, rating: 5, label: 'Excellent' },
];

export const FeedbackUI: React.FC<FeedbackUIProps> = ({
    onSubmit,
    primaryColor,
    secondaryColor,
}) => {
    const [selectedRating, setSelectedRating] = useState<number | null>(null);
    const [hoveredRating, setHoveredRating] = useState<number | null>(null);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showThankYou, setShowThankYou] = useState(false);

    const handleSubmit = async () => {
        if (!selectedRating || isSubmitting || showThankYou) return;
        setIsSubmitting(true);
        try {
            await onSubmit(selectedRating, comment.trim() || undefined);
            setShowThankYou(true);
        } catch (error) {
            console.error('[v2] Error submitting feedback:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSkip = () => {
        if (isSubmitting || showThankYou) return;
        setShowThankYou(true);
    };

    const getAccent = (rating: number) => {
        if (rating === 3) return '#f59e0b';
        if (rating === 4) return '#22c55e';
        return '#10b981';
    };

    if (showThankYou) {
        return (
            <div
                style={{
                    background: '#fff',
                    borderRadius: 14,
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    padding: '20px 16px',
                    textAlign: 'center',
                }}
            >
                <div style={{ marginBottom: 8 }}>
                    <Smile size={28} style={{ color: primaryColor }} strokeWidth={2} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: primaryColor, marginBottom: 4 }}>
                    Thank you!
                </div>
                <div style={{ fontSize: 11, color: '#6b7280', lineHeight: '1.5' }}>
                    We appreciate your feedback.
                </div>
                <div
                    style={{
                        marginTop: 12,
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: `${primaryColor}08`,
                        border: `1px solid ${primaryColor}18`,
                        fontSize: 11,
                        color: '#6b7280',
                        lineHeight: '1.5',
                    }}
                >
                    Want{' '}
                    <a href="https://www.zentri.ai" target="_blank" rel="noopener noreferrer"
                        style={{ color: primaryColor, fontWeight: 600, textDecoration: 'underline' }}>Zentri chatbot</a>{' '}
                    for your business?{' '}
                    <a href="https://www.zentri.ai/#contact-us" target="_blank" rel="noopener noreferrer"
                        style={{ color: primaryColor, fontWeight: 600, textDecoration: 'underline' }}>Click here</a>.
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                background: '#fff',
                borderRadius: 14,
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                position: 'relative',
            }}
        >
            {/* Gradient accent */}
            <div style={{ height: 3, background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})` }} />

            <div style={{ padding: '14px 16px 16px' }}>
                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: primaryColor }}>
                        How was your experience?
                    </div>
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                        Your feedback helps us improve
                    </div>
                </div>

                {/* Rating icons */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginBottom: 12 }}>
                    {RATING_OPTIONS.map(({ Icon, rating, label }) => {
                        const isSelected = selectedRating === rating;
                        const isHovered = hoveredRating === rating;
                        const accent = getAccent(rating);
                        return (
                            <div key={rating} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                                <button
                                    onClick={() => !isSubmitting && setSelectedRating(rating)}
                                    onMouseEnter={() => setHoveredRating(rating)}
                                    onMouseLeave={() => setHoveredRating(null)}
                                    disabled={isSubmitting}
                                    aria-label={label}
                                    style={{
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        width: 42,
                                        height: 42,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                        padding: 0,
                                        outline: 'none',
                                        border: isSelected ? `2px solid ${accent}` : '2px solid #e5e7eb',
                                        background: isSelected ? `${accent}14` : isHovered ? `${accent}08` : '#f9fafb',
                                        boxShadow: isSelected ? `0 3px 10px ${accent}25` : 'none',
                                        transform: isSelected ? 'scale(1.1)' : isHovered ? 'scale(1.05)' : 'scale(1)',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <Icon size={22} style={{ color: isSelected || isHovered ? accent : primaryColor }} strokeWidth={2} />
                                </button>
                                <span style={{ fontSize: 10, fontWeight: 600, color: isSelected || isHovered ? accent : '#9ca3af' }}>
                                    {label}
                                </span>
                            </div>
                        );
                    })}
                </div>

                {/* Comment */}
                <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
                        Optional comment
                    </div>
                    <textarea
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        onFocus={applyNoZoomViewport}
                        onBlur={restoreViewport}
                        placeholder="Tell us more..."
                        disabled={isSubmitting}
                        rows={2}
                        style={{
                            width: '100%',
                            boxSizing: 'border-box',
                            resize: 'none',
                            padding: '6px 10px',
                            fontSize: 12,
                            lineHeight: '1.4',
                            color: '#111827',
                            border: `1.5px solid ${primaryColor}30`,
                            borderRadius: 8,
                            outline: 'none',
                            background: '#fff',
                            display: 'block',
                        }}
                    />
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={handleSkip}
                        disabled={isSubmitting}
                        style={{
                            flex: 1,
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            padding: '7px 0',
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 8,
                            cursor: 'pointer',
                            border: `1.5px solid ${primaryColor}30`,
                            color: primaryColor,
                            background: `${primaryColor}06`,
                            outline: 'none',
                        }}
                    >
                        Skip
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={!selectedRating || isSubmitting}
                        style={{
                            flex: 1,
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            padding: '7px 0',
                            fontSize: 12,
                            fontWeight: 600,
                            borderRadius: 8,
                            cursor: selectedRating && !isSubmitting ? 'pointer' : 'not-allowed',
                            border: 'none',
                            color: '#fff',
                            outline: 'none',
                            background: selectedRating && !isSubmitting
                                ? `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`
                                : '#d1d5db',
                            opacity: !selectedRating || isSubmitting ? 0.7 : 1,
                        }}
                    >
                        {isSubmitting ? 'Sending...' : 'Submit'}
                    </button>
                </div>
            </div>
        </div>
    );
};
